import type { BookingSearchRow } from '@/lib/types';
import { cn } from '@/lib/utils';
import { Cat, ChevronDown, ChevronRight, Dog } from 'lucide-react';
import { Link } from 'react-router-dom';
import { StatusPill } from './StatusPill';

interface Props {
  rows: BookingSearchRow[];
  expanded: boolean;
  onToggle: () => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function SpeciesIcon({ species }: { species: string }) {
  const Icon = species === 'cat' ? Cat : Dog;
  return <Icon className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.5} aria-hidden />;
}

export function BookingGroupRow({ rows, expanded, onToggle }: Props) {
  const primary = rows[0];
  const siblings = rows.slice(1);

  if (!primary) return null;

  return (
    <>
      {/* Primary row */}
      <tr
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onToggle();
        }}
        tabIndex={0}
        className={cn(
          'cursor-pointer hover:bg-secondary/40 transition-colors',
          expanded && 'bg-secondary/30',
        )}
      >
        <td className="py-2.5 pl-4 pr-2 w-6">
          <span className="text-muted-foreground">
            {expanded ? (
              <ChevronDown className="size-4" strokeWidth={1.75} />
            ) : (
              <ChevronRight className="size-4" strokeWidth={1.75} />
            )}
          </span>
        </td>
        <td className="py-2.5 px-2 text-sm font-medium">{primary.customerName}</td>
        <td className="py-2.5 px-2">
          <div className="flex items-center gap-1.5 text-sm">
            <SpeciesIcon species={primary.species} />
            {primary.petName}
            {siblings.length > 0 && (
              <span className="text-xs text-muted-foreground">+{siblings.length}</span>
            )}
          </div>
        </td>
        <td className="py-2.5 px-2 text-sm num text-muted-foreground">
          {formatDate(primary.checkIn)}
        </td>
        <td className="py-2.5 px-2 text-sm num text-muted-foreground">
          {formatDate(primary.checkout)}
        </td>
        <td className="py-2.5 px-2 text-sm text-muted-foreground">{primary.runName ?? '—'}</td>
        <td className="py-2.5 px-2">
          <StatusPill status={primary.status} />
        </td>
        <td className="py-2.5 px-2 text-sm text-muted-foreground">{primary.servicesCsv ?? '—'}</td>
        <td className="py-2.5 px-2">
          <Link
            to={`/bookings/${primary.bookingGroupId ?? primary.bookingId}`}
            onClick={(e) => e.stopPropagation()}
            className="text-xs font-medium text-primary hover:underline"
          >
            View
          </Link>
        </td>
      </tr>

      {/* Sibling rows (multi-pet groups) — always visible as indented sub-rows */}
      {siblings.map((row) => (
        <tr key={row.bookingId} className={cn(expanded ? '' : 'hidden')}>
          <td />
          <td />
          <td className="py-1.5 px-2">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground pl-3 border-l-2 border-border ml-1">
              <SpeciesIcon species={row.species} />
              {row.petName}
            </div>
          </td>
          <td className="py-1.5 px-2 text-sm num text-muted-foreground">
            {formatDate(row.checkIn)}
          </td>
          <td className="py-1.5 px-2 text-sm num text-muted-foreground">
            {formatDate(row.checkout)}
          </td>
          <td className="py-1.5 px-2 text-sm text-muted-foreground">{row.runName ?? '—'}</td>
          <td className="py-1.5 px-2">
            <StatusPill status={row.status} />
          </td>
          <td className="py-1.5 px-2 text-sm text-muted-foreground">{row.servicesCsv ?? '—'}</td>
        </tr>
      ))}
    </>
  );
}
